import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "harvest-traceability-systems",
  kind: "domain-vertical",
  title: "Harvest Tracking & Lot Traceability Systems",
  shortTitle: "Harvest Traceability",
  status: "draft",
  tier: "we-do",
  seoTitle: "Harvest Tracking & Lot Traceability Systems | GA",
  seoDescription:
    "Harvest yield logging, pack-out grading, batch lot code generation, and farm-to-table traceability records for commercial agricultural producers.",
  summary:
    "Harvest yield tracking, pack-out grading, batch lot code generation, and cold-storage inventory handoffs that provide complete seed-to-sale traceability.",
  image: "/images/blog/reconciling-fragmented-data-hero.webp",
  relatedCapabilitySlugs: [],
  relatedIndustrySlugs: ["agriculture"],
  relatedPostSlugs: [
    "reconciling-fragmented-data",
    "dashboard-design",
    "from-bed-grid-to-packing-shed",
  ],
  engineering: [
    "Harvest recording interfaces capturing gross field weight, cull weight, and graded pack-out metrics",
    "Automated harvest batch and lot code generation linking picking dates directly to planting beds",
    "Post-harvest cold-storage inventory handoffs tracking pallet locations, cooling curves, and shelf-life",
    "Food safety compliance traceability linking pack-out lots to harvest crews, wash lines, and buyer orders",
    "Actual-versus-projected yield variance analysis by cultivar, succession, and individual field block",
    "Label generation engines printing GS1-compliant barcodes, QR codes, and traceback placards for wholesale crates",
  ],
  body: [
    {
      type: "heading",
      level: 2,
      text: "Traceability Starts in the Field at the Harvest Bin",
    },
    {
      type: "paragraph",
      text: "Modern commercial farming operates under strict food safety mandates (FSMA Rule 204) and rigorous customer quality specifications. When harvest yields are scribbled on cardboard boxes or tracked across detached spreadsheets, isolating a contaminated batch or resolving a buyer quality claim requires broad, expensive recalls that damage customer trust and discard sound produce.",
    },
    {
      type: "heading",
      level: 2,
      text: "Closing the Loop on Crop Economics and Field Performance",
    },
    {
      type: "paragraph",
      text: "A disciplined harvest and traceability platform captures gross field weight, pack-out percentages, and cull reasons immediately upon picking. By assigning standardized lot codes that link wholesale shipments back through the wash line to specific planting beds, harvest crews, and seed varieties, farms achieve audit-proof traceability while uncovering the true financial return per linear bed foot.",
    },
  ],
  phases: {
    study: {
      focus:
        "Audit field harvest routines, packhouse wash and pack lines, lot-coding methods, cold storage, and buyer compliance requirements.",
      produces: [
        "Harvest workflow, crate-weighing, and post-harvest handling process map",
        "Traceability and regulatory compliance requirements audit (FSMA 204, GAP)",
        "Pack-out grading standards and cull categorization taxonomy",
        "Cold storage bin tracking and inventory handoff analysis",
      ],
      fit: {
        good: [
          "Commercial vegetable, fruit, and specialty crop growers supplying grocery, wholesale, restaurant, or direct-to-consumer channels",
          "Farms needing audit-proof lot traceability, automated barcode labeling, and actual-versus-expected yield metrics",
        ],
        poor: [
          "Bulk grain or forage operations where individual crate-level lot tracking and pack-out grading are inapplicable",
          "Casual gardens without commercial harvest distribution, food-safety audit requirements, or sales channels",
        ],
      },
    },
    "initial-design": {
      focus:
        "Design harvest lot data schemas, barcode/QR encoding schemes, cold-storage inventory models, and scale integration contracts.",
      produces: [
        "Harvest batch and lot-traceability relational schema design",
        "GS1-compliant lot identifier and barcode generation specifications",
        "Post-harvest inventory location models (coolers, staging, delivery vehicles)",
        "Mobile harvest-entry and packhouse scale-interface wireframes",
      ],
    },
    "detailed-design": {
      focus:
        "Specify weighing scale hardware protocols, pallet label layouts, traceback query algorithms, and buyer EDI interfaces.",
      produces: [
        "Scale integration and gross/tare/net weight recording specifications",
        "Crate and pallet label printing templates and zebra printer integration contracts",
        "Bidirectional traceback query algorithms connecting customer invoices to field beds",
        "Actual yield variance and bed-profitability reporting schemas",
      ],
    },
    implementation: {
      focus:
        "Build and deploy the harvest tracking application, install packhouse label printers, configure scales, and train pack crews.",
      produces: [
        "Production harvest tracking and lot traceability application",
        "Configured lot-generation rules and active crate-labeling workflows",
        "Integrated packhouse weighing and cold-storage inventory registers",
        "Food safety audit runbooks and mock-recall test documentation",
      ],
    },
  },
};
